import React from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import './MorphDialog.scss';
import { useEscapeClose, useLockBodyScroll } from '../Sheet/useDialogEffects';

export interface MorphDialogProps {
    open: boolean;
    onClose: () => void;
    children: React.ReactNode;
    ariaLabel?: string;
    className?: string;
}

const ENTER_STATE = { opacity: 0, scale: 0.96, y: 12 };
const VISIBLE_STATE = { opacity: 1, scale: 1, y: 0 };
function MorphDialog({
    open,
    onClose,
    children,
    ariaLabel,
    className,
}: MorphDialogProps): React.ReactElement | null {
    const reduced = useReducedMotion();
    useEscapeClose(open, onClose);
    useLockBodyScroll(open);

    if (typeof document === 'undefined') return null;

    const transition = {
        duration: reduced ? 0 : 0.26,
        ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
    };

    return createPortal(
        <AnimatePresence initial={false}>
            {open ? (
                <div className="rd-dialog-layer">
                    <motion.div
                        className="rd-dialog-backdrop"
                        onClick={onClose}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: reduced ? 0 : 0.18 }}
                    />
                    <motion.div
                        className={['rd-dialog', className]
                            .filter(Boolean)
                            .join(' ')}
                        role="dialog"
                        aria-modal="true"
                        aria-label={ariaLabel}
                        initial={reduced ? false : ENTER_STATE}
                        animate={VISIBLE_STATE}
                        exit={reduced ? undefined : ENTER_STATE}
                        transition={transition}
                    >
                        <button
                            type="button"
                            className="rd-dialog__close"
                            onClick={onClose}
                            aria-label="Закрыть"
                        >
                            <span className="rd-dialog__close-bar" />
                            <span className="rd-dialog__close-bar" />
                        </button>
                        <div className="rd-dialog__body">{children}</div>
                    </motion.div>
                </div>
            ) : null}
        </AnimatePresence>,
        document.body
    );
}

MorphDialog.defaultProps = {
    ariaLabel: undefined,
    className: undefined,
};

export default React.memo(MorphDialog);
